import { useRef, useState, useEffect } from 'react'
import { KEYBOARD_KEY, DOM_EVENT } from '@/shared/constants/keyboard'

import type { SelectOption } from './index'

interface UseSelectFieldProps {
  onBlur?: () => void
  onOpenChange?: (open: boolean) => void
  onSearch?: (term: string) => void
  options: SelectOption[]
  value: string
  searchable: boolean
  loading: boolean
  hasOptionsPlaceholder?: boolean
}

export function useSelectField({
  onBlur,
  onOpenChange,
  onSearch,
  options,
  value,
  searchable,
  loading,
  hasOptionsPlaceholder = false,
}: UseSelectFieldProps) {
  const externalSearch = !!onSearch
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    onOpenChange?.(isOpen)
  }, [isOpen])
  const [search, setSearch] = useState('')
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const ref = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const optionRefs = useRef<(HTMLLIElement | undefined)[]>([])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
        setSearch('')
      }
    }
    document.addEventListener(DOM_EVENT.MOUSEDOWN, handleClickOutside)
    return () =>
      document.removeEventListener(DOM_EVENT.MOUSEDOWN, handleClickOutside)
  }, [])

  useEffect(() => {
    if (isOpen) {
      if (searchable) {
        setTimeout(() => searchRef.current?.focus(), 0)
      } else {
        const selectedIdx = options.findIndex((o) => o.value === value)
        const idx = selectedIdx >= 0 ? selectedIdx : 0
        setFocusedIndex(idx)
        setTimeout(() => optionRefs.current[idx]?.focus(), 0)
      }
    } else {
      setSearch('')
      setFocusedIndex(-1)
    }
  }, [isOpen])

  // Reset list focus when search text changes
  useEffect(() => {
    setFocusedIndex(-1)
  }, [search])

  function toggle() {
    setIsOpen((o) => !o)
  }

  function handleSearchChange(term: string) {
    setSearch(term)
    onSearch?.(term)
  }

  function select(onSelect: (value: string) => void, val: string) {
    onSelect(val)
    setIsOpen(false)
    setSearch('')
    setFocusedIndex(-1)
    onBlur?.()
  }

  // Handles keydown on the search input
  function handleSearchKeyDown(
    e: React.KeyboardEvent,
    onSelect: (value: string) => void,
    filteredOptions: SelectOption[],
  ) {
    if (e.key === KEYBOARD_KEY.ENTER) {
      e.preventDefault()
      const first = filteredOptions[0]
      if (first) {
        select(onSelect, first.value)
      }
    } else if (e.key === KEYBOARD_KEY.ARROW_DOWN) {
      e.preventDefault()
      if (filteredOptions.length > 0) {
        setFocusedIndex(0)
        optionRefs.current[0]?.focus()
      }
    } else if (e.key === KEYBOARD_KEY.ESCAPE) {
      e.preventDefault()
      setIsOpen(false)
    }
  }

  // Handles keydown on each <li> option
  function handleKeyDown(
    e: React.KeyboardEvent,
    onSelect: (value: string) => void,
    optionValue: string,
    filteredOptions: SelectOption[],
    index: number,
  ) {
    if (e.key === KEYBOARD_KEY.ENTER || e.key === KEYBOARD_KEY.SPACE) {
      e.preventDefault()
      select(onSelect, optionValue)
    } else if (e.key === KEYBOARD_KEY.ARROW_DOWN) {
      e.preventDefault()
      const next = index + 1
      if (next < filteredOptions.length) {
        setFocusedIndex(next)
        optionRefs.current[next]?.focus()
      }
    } else if (e.key === KEYBOARD_KEY.ARROW_UP) {
      e.preventDefault()
      const prev = index - 1
      if (prev >= 0) {
        setFocusedIndex(prev)
        optionRefs.current[prev]?.focus()
      } else if (searchable) {
        setFocusedIndex(-1)
        searchRef.current?.focus()
      }
    } else if (e.key === KEYBOARD_KEY.ESCAPE) {
      e.preventDefault()
      setIsOpen(false)
    }
  }

  const selectedLabel = options.find((o) => o.value === value)?.label
  const filteredOptions =
    searchable && !externalSearch
      ? options.filter((o) =>
          o.label.toLowerCase().includes(search.toLowerCase()),
        )
      : options

  // For async search (onSearch), keep the dropdown hidden until there's a
  // query or results to show, so we never flash an empty panel while idle.
  // When an optionsPlaceholder is provided, keep the panel open while focused
  // (including idle/loading) so the placeholder hint is shown.
  const showDropdown =
    isOpen &&
    (searchable && onSearch
      ? hasOptionsPlaceholder ||
        ((!!search.trim() || filteredOptions.length > 0) && !loading)
      : true)

  return {
    ref,
    isOpen,
    toggle,
    select,
    handleKeyDown,
    handleSearchKeyDown,
    search,
    setSearch,
    handleSearchChange,
    searchRef,
    optionRefs,
    selectedLabel,
    filteredOptions,
    focusedIndex,
    showDropdown,
  }
}
